import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth, getStoredToken } from "../auth/AuthContext";

/**
 * Back link that routes to /dashboard when the user is logged in,
 * and to / (landing) otherwise. Fixes the perceived "redirects me to
 * login every time I push the back arrow" bug — logged-in users hitting
 * the back arrow used to be sent to the public marketing site, which
 * shows "Try Ascend Higher" + "Log In" prominently and felt like a logout.
 *
 * Uses a synchronous getStoredToken() fallback during AuthContext hydration
 * so the link target is decided correctly even on first paint after a
 * page reload — no race condition between user-state and render.
 */
export default function SmartBackLink({ label, className, "data-testid": testId }) {
  const { user } = useAuth();
  // Synchronous fallback: if there's a token in localStorage we're almost
  // certainly logged in, even if useAuth() hasn't finished hydrating yet.
  const looksLoggedIn = !!user || !!getStoredToken();
  const target = looksLoggedIn ? "/dashboard" : "/";
  const displayLabel = label ?? (looksLoggedIn ? "Dashboard" : "Back");
  return (
    <Link
      to={target}
      data-testid={testId || "smart-back-link"}
      className={
        className ||
        "inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]"
      }
    >
      <ArrowLeft size={16} /> {displayLabel}
    </Link>
  );
}
